import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGears } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../AxiosInstance';
import styles from './scada.module.css';

// Inline SVG previews for palette tiles (small, 36x36)
import { TankSVG } from '../../nodes/scada/TankNode';

import { PumpSVG } from '../../nodes/scada/PumpNode';
import { MotorSVG } from '../../nodes/scada/MotorNode';
import { ValveSVG } from '../../nodes/scada/ValveNode';
import { Valve3WaySVG } from '../../nodes/scada/Valve3WayNode';
import { DigitalDisplaySVG } from '../../nodes/scada/DigitalDisplayNode';
import { LEDSVGIcon } from '../../nodes/scada/LEDIndicatorNode';
import { TempGaugeSVG } from '../../nodes/scada/TemperatureGaugeNode';
import { PressureGaugeSVG } from '../../nodes/scada/PressureGaugeNode';
import { FlowMeterSVG } from '../../nodes/scada/FlowMeterNode';
import { CycloneSVG } from '../../nodes/scada/CycloneNode';
import { RibbonBlenderSVG } from '../../nodes/scada/RibbonBlenderNode';
import { HopperScrewSVG } from '../../nodes/scada/HopperScrewNode';
import { GranulatorSVG } from '../../nodes/scada/GranulatorNode';
import { ScrewConveyorSVG } from '../../nodes/scada/ScrewConveyorNode';
import { BucketElevatorSVG } from '../../nodes/scada/BucketElevatorNode';
import { PackagerSingleSVG } from '../../nodes/scada/PackagerSingleNode';
import { PackagerDoypakSVG } from '../../nodes/scada/PackagerDoypakNode';
import { ChillerSVG } from '../../nodes/scada/ChillerNode';
import { CompressorSVG } from '../../nodes/scada/CompressorNode';
import { BoilerSVG } from '../../nodes/scada/BoilerNode';
import { WaterHeaterSVG } from '../../nodes/scada/WaterHeaterNode';
import { VBlenderSVG } from '../../nodes/scada/VBlenderNode';
import { SieveSVG } from '../../nodes/scada/SieveNode';
import { BeltConveyorSVG } from '../../nodes/scada/BeltConveyorNode';
import { CipSkidSVG } from '../../nodes/scada/CipSkidNode';
import { BoxFormerSVG } from '../../nodes/scada/BoxFormerNode';
import { StripFolderSVG } from '../../nodes/scada/StripFolderNode';
import { BoxSealerSVG } from '../../nodes/scada/BoxSealerNode';

const PALETTE_SECTIONS = [
    {
        key: 'process',
        items: [
            { type: 'tank', labelKey: 'scada.elements.tank', defaultW: 60, defaultH: 80, Preview: () => <TankSVG value={50} config={{ min_value: 0, max_value: 100 }} width={36} height={46} /> },
            { type: 'pump', labelKey: 'scada.elements.pump', defaultW: 50, defaultH: 50, Preview: () => <PumpSVG value={0} config={{}} width={36} height={36} /> },
            { type: 'motor', labelKey: 'scada.elements.motor', defaultW: 50, defaultH: 40, Preview: () => <MotorSVG value={0} config={{}} width={36} height={28} /> },
            { type: 'valve_gate', labelKey: 'scada.elements.valve_gate', defaultW: 40, defaultH: 40, Preview: () => <ValveSVG value={0} config={{}} width={32} height={32} /> },
            { type: 'valve_ball', labelKey: 'scada.elements.valve_ball', defaultW: 36, defaultH: 36, Preview: () => <ValveSVG value={1} config={{}} width={32} height={32} type="valve_ball" /> },
            { type: 'valve_3way', labelKey: 'scada.elements.valve_3way', defaultW: 60, defaultH: 60, Preview: () => <Valve3WaySVG value={1} config={{}} width={36} height={36} /> },
        ],
    },
    {
        key: 'equipamiento_auxiliar',
        items: [
            { type: 'chiller',       labelKey: 'scada.elements.chiller',       defaultW: 120, defaultH: 80, Preview: () => <ChillerSVG liveValues={{}} config={{}} width={54} height={36} /> },
            { type: 'compressor',    labelKey: 'scada.elements.compressor',    defaultW: 80,  defaultH: 60, Preview: () => <CompressorSVG liveValues={{}} config={{}} width={40} height={30} /> },
            { type: 'boiler',        labelKey: 'scada.elements.boiler',        defaultW: 70,  defaultH: 100, Preview: () => <BoilerSVG liveValues={{}} config={{}} width={32} height={46} /> },
            { type: 'water_heater',  labelKey: 'scada.elements.water_heater',  defaultW: 60,  defaultH: 85, Preview: () => <WaterHeaterSVG liveValues={{}} config={{}} width={28} height={40} /> },
            { type: 'cip_skid',      labelKey: 'scada.elements.cip_skid',      defaultW: 160, defaultH: 100, Preview: () => <CipSkidSVG liveValues={{}} config={{}} width={54} height={34} /> },
        ],
    },
    {
        key: 'food_industry',
        items: [
            { type: 'cyclone',         labelKey: 'scada.elements.cyclone',         defaultW: 60,  defaultH: 80,  Preview: () => <CycloneSVG value={0} config={{}} width={36} height={48} /> },
            { type: 'ribbon_blender',  labelKey: 'scada.elements.ribbon_blender',  defaultW: 100, defaultH: 60,  Preview: () => <RibbonBlenderSVG value={0} config={{}} width={52} height={32} /> },
            { type: 'cooler_blender',  labelKey: 'scada.elements.cooler_blender',  defaultW: 110, defaultH: 70,  Preview: () => <RibbonBlenderSVG value={0} config={{}} width={52} height={32} showJacket /> },
            { type: 'v_blender',       labelKey: 'scada.elements.v_blender',       defaultW: 90,  defaultH: 75,  Preview: () => <VBlenderSVG liveValues={{}} config={{}} width={46} height={38} /> },
            { type: 'hopper_screw',    labelKey: 'scada.elements.hopper_screw',    defaultW: 60,  defaultH: 90,  Preview: () => <HopperScrewSVG value={0} config={{}} width={36} height={54} /> },
            { type: 'granulator',      labelKey: 'scada.elements.granulator',      defaultW: 70,  defaultH: 80,  Preview: () => <GranulatorSVG value={0} config={{}} width={40} height={46} /> },
            { type: 'sieve',           labelKey: 'scada.elements.sieve',           defaultW: 80,  defaultH: 55,  Preview: () => <SieveSVG liveValues={{}} config={{}} width={42} height={30} /> },
            { type: 'screw_conveyor',  labelKey: 'scada.elements.screw_conveyor',  defaultW: 120, defaultH: 36,  Preview: () => <ScrewConveyorSVG value={0} config={{}} width={54} height={22} /> },
            { type: 'belt_conveyor',   labelKey: 'scada.elements.belt_conveyor',   defaultW: 120, defaultH: 40,  Preview: () => <BeltConveyorSVG liveValues={{}} config={{}} width={54} height={20} /> },
            { type: 'bucket_elevator', labelKey: 'scada.elements.bucket_elevator', defaultW: 120, defaultH: 50,  Preview: () => <BucketElevatorSVG config={{}} liveValues={{}} width={54} height={24} /> },
            { type: 'packager_single', labelKey: 'scada.elements.packager_single', defaultW: 55,  defaultH: 100, Preview: () => <PackagerSingleSVG value={0} config={{}} width={32} height={54} lanes={1} /> },
            { type: 'packager_multi',  labelKey: 'scada.elements.packager_multi',  defaultW: 100, defaultH: 100, Preview: () => <PackagerSingleSVG value={0} config={{ lanes: 3 }} width={54} height={54} lanes={3} /> },
            { type: 'packager_doypack',labelKey: 'scada.elements.packager_doypack',defaultW: 120, defaultH: 55,  Preview: () => <PackagerDoypakSVG value={0} config={{}} width={54} height={28} /> },
            { type: 'box_former',      labelKey: 'scada.elements.box_former',      defaultW: 90,  defaultH: 70,  Preview: () => <BoxFormerSVG liveValues={{}} config={{}} width={46} height={36} /> },
            { type: 'strip_folder',    labelKey: 'scada.elements.strip_folder',    defaultW: 100, defaultH: 90,  Preview: () => <StripFolderSVG liveValues={{}} config={{}} width={50} height={44} /> },
            { type: 'box_sealer',      labelKey: 'scada.elements.box_sealer',      defaultW: 90,  defaultH: 70,  Preview: () => <BoxSealerSVG liveValues={{}} config={{}} width={46} height={36} /> },
        ],
    },
    {
        key: 'sensors',
        items: [
            { type: 'sensor_temp', labelKey: 'scada.elements.sensor_temp', defaultW: 50, defaultH: 70, Preview: () => <TempGaugeSVG value={25} config={{}} width={28} height={42} /> },
            { type: 'sensor_pressure', labelKey: 'scada.elements.sensor_pressure', defaultW: 60, defaultH: 60, Preview: () => <PressureGaugeSVG value={5} config={{ min_value: 0, max_value: 10 }} width={36} height={36} /> },
            { type: 'sensor_flow', labelKey: 'scada.elements.sensor_flow', defaultW: 60, defaultH: 40, Preview: () => <FlowMeterSVG value={10} config={{}} width={36} height={24} /> },
            { type: 'sensor_level', labelKey: 'scada.elements.sensor_level', defaultW: 30, defaultH: 80, Preview: () => <div style={{ width: 18, height: 48, background: '#1E293B', border: '1px solid #475569', borderRadius: 3, position: 'relative', overflow: 'hidden' }}><div style={{ position: 'absolute', bottom: 0, width: '100%', height: '60%', background: '#22C55E', opacity: 0.8 }} /></div> },
            { type: 'sensor_humidity', labelKey: 'scada.elements.sensor_humidity', defaultW: 50, defaultH: 50, Preview: () => <svg width={36} height={36}><circle cx={18} cy={18} r={14} fill="#1E293B" stroke="#3B82F6" strokeWidth={2} /><path d="M18,8 Q27,14 27,20 Q27,28 18,30 Q9,28 9,20 Q9,14 18,8" fill="#3B82F6" opacity={0.7} /></svg> },
        ],
    },
    {
        key: 'display',
        items: [
            { type: 'display_digital', labelKey: 'scada.elements.display_digital', defaultW: 80, defaultH: 40, Preview: () => <DigitalDisplaySVG value={42.5} config={{ unit: '°C' }} width={54} height={28} /> },
            { type: 'display_trend', labelKey: 'scada.elements.display_trend', defaultW: 120, defaultH: 60, Preview: () => <div style={{ width: 54, height: 32, background: '#000', borderRadius: 2, border: '1px solid #334155', display: 'flex', alignItems: 'flex-end', gap: 2, padding: '2px 4px' }}>{[4, 8, 5, 12, 9, 15, 11].map((h, i) => <div key={i} style={{ flex: 1, height: `${h * 2}px`, background: '#4F46E5', borderRadius: 1 }} />)}</div> },
            { type: 'led_indicator', labelKey: 'scada.elements.led_indicator', defaultW: 32, defaultH: 32, Preview: () => <LEDSVGIcon value={1} config={{ threshold: 0.5, on_color: '#22C55E' }} size={32} /> },
            { type: 'alarm_banner', labelKey: 'scada.elements.alarm_banner', defaultW: 120, defaultH: 32, Preview: () => <div style={{ width: 54, height: 20, background: '#EF4444', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700 }}>ALARM</div> },
        ],
    },
    {
        key: 'shapes',
        items: [
            { type: 'shape_rect', labelKey: 'scada.elements.shape_rect', defaultW: 80, defaultH: 60, Preview: () => <div style={{ width: 36, height: 28, border: '2px solid #475569', borderRadius: 3 }} /> },
            { type: 'shape_circle', labelKey: 'scada.elements.shape_circle', defaultW: 60, defaultH: 60, Preview: () => <div style={{ width: 32, height: 32, border: '2px solid #475569', borderRadius: '50%' }} /> },
            { type: 'shape_line', labelKey: 'scada.elements.shape_line', defaultW: 80, defaultH: 20, Preview: () => <svg width={36} height={16}><line x1={4} y1={8} x2={30} y2={8} stroke="#475569" strokeWidth={2} markerEnd="none" /><polygon points="28,4 36,8 28,12" fill="#475569" /></svg> },
            { type: 'text_label', labelKey: 'scada.elements.text_label', defaultW: 80, defaultH: 30, Preview: () => <div style={{ fontSize: 13, color: '#F1F5F9', fontWeight: 700 }}>Abc</div> },
            { type: 'image_embed', labelKey: 'scada.elements.image_embed', defaultW: 80, defaultH: 80, Preview: () => <div style={{ width: 32, height: 32, border: '1px dashed #475569', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🖼</div> },
        ],
    },
];

function PaletteTile({ item, onDragStart }) {
    const { t } = useTranslation();
    return (
        <div
            className={styles.paletteTile}
            draggable
            onDragStart={e => {
                e.dataTransfer.setData('application/scada-element-type', item.type);
                e.dataTransfer.setData('application/scada-element-w', item.defaultW);
                e.dataTransfer.setData('application/scada-element-h', item.defaultH);
                if (onDragStart) onDragStart(item);
            }}
        >
            <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <item.Preview />
            </div>
            <div className={styles.paletteTileLabel}>{t(item.labelKey, item.type)}</div>
        </div>
    );
}

function CustomMachineTile({ mt, onDragStart }) {
    return (
        <div
            className={styles.paletteTile}
            draggable
            onDragStart={e => {
                e.dataTransfer.setData('application/scada-element-type', 'custom_machine');
                e.dataTransfer.setData('application/scada-element-w', '80');
                e.dataTransfer.setData('application/scada-element-h', '100');
                e.dataTransfer.setData('application/scada-machine-type-id', String(mt.id));
                if (onDragStart) onDragStart(mt);
            }}
        >
            <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {mt.image_url
                    ? <img src={mt.image_url} alt={mt.name} style={{ maxWidth: 36, maxHeight: 36, objectFit: 'contain' }} />
                    : <FontAwesomeIcon icon={faGears} style={{ color: '#4F46E5', fontSize: 20 }} />
                }
            </div>
            <div className={styles.paletteTileLabel}>{mt.name}</div>
        </div>
    );
}

export default function SCADAElementPalette({ projectId, onShowMachineTypes }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [machineTypes, setMachineTypes] = useState([]);

    useEffect(() => {
        if (!projectId) return;
        axiosInstance.get(`scada_machine_type/?project_id=${projectId}`).then(res => {
            setMachineTypes(res.data.results || res.data);
        });
    }, [projectId]);

    const matchesSearch = (labelKey, type) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return type.includes(q) || t(labelKey, '').toLowerCase().includes(q);
    };

    return (
        <div className={styles.palette}>
            <div className={styles.paletteSearch}>
                <input
                    className={styles.paletteInput}
                    placeholder={t('common.search') || 'Search...'}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {PALETTE_SECTIONS.map(section => {
                const visible = section.items.filter(item => matchesSearch(item.labelKey, item.type));
                if (visible.length === 0) return null;
                return (
                    <div key={section.key} className={styles.paletteSection}>
                        <div className={styles.paletteSectionHeader}>
                            {t(`scada.categories.${section.key}`, section.key)}
                        </div>
                        <div className={styles.paletteTileGrid}>
                            {visible.map(item => <PaletteTile key={item.type} item={item} />)}
                        </div>
                    </div>
                );
            })}

            {/* Custom machines section */}
            <div className={styles.paletteSection}>
                <div className={styles.paletteSectionHeader} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 8 }}>
                    <span>{t('scada.categories.custom')}</span>
                    <button
                        onClick={onShowMachineTypes}
                        style={{ background: 'none', border: 'none', color: '#4F46E5', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}
                    >
                        {t('scada.manageMachineTypesBtn')}
                    </button>
                </div>
                <div className={styles.paletteTileGrid}>
                    {machineTypes
                        .filter(mt => matchesSearch(mt.name, mt.name))
                        .map(mt => <CustomMachineTile key={mt.id} mt={mt} />)
                    }
                </div>
            </div>
        </div>
    );
}
